import { API } from "@/config/apiBase";
import React, { useState, useEffect, useContext } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Phone, CheckCircle, XCircle, Loader2, ArrowRight, Sparkles, Gift
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { AuthContext } from "@/App";
import confetti from 'canvas-confetti';


const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  const [status, setStatus] = useState("checking"); // checking, success, failed
  const [paymentData, setPaymentData] = useState(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus("failed");
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("failed");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      setPaymentData(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");
        triggerConfetti();
      } else if (response.data.status === "expired") {
        setStatus("failed");
      } else {
        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    }
  };

  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#E07A5F', '#81B29A', '#F2CC8F', '#3D405B']
    });
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4">
      <motion.div 
        className="max-w-md w-full bg-white rounded-3xl p-8 shadow-xl border border-[#F4F1EA] text-center"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 bg-[#E07A5F] rounded-full flex items-center justify-center">
            <Phone className="w-7 h-7 text-white" />
          </div>
        </div>

        {status === "checking" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#F2CC8F]/20 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-[#F2CC8F] animate-spin" />
            </div>
            <h1 
              className="text-2xl font-bold text-[#3D405B] mb-2"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Processing Payment...
            </h1>
            <p className="text-[#6D6F7C]">
              Please wait while we confirm your payment
            </p>
          </motion.div>
        )}

        {status === "success" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#81B29A]/20 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-[#81B29A]" />
            </div>
            <h1 
              className="text-2xl font-bold text-[#3D405B] mb-2"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Payment Successful! 🎉
            </h1>
            <p className="text-[#6D6F7C] mb-6">
              Thank you for your purchase. Your account has been updated.
            </p>

            {paymentData && (
              <div className="bg-[#FDFBF7] rounded-2xl p-4 mb-6 text-left">
                <div className="flex justify-between mb-2">
                  <span className="text-[#6D6F7C]">Amount</span>
                  <span className="font-semibold text-[#3D405B]">
                    ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#6D6F7C]">Status</span>
                  <span className="font-semibold text-[#81B29A]">Completed</span>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate("/dashboard")}
                className="w-full bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full py-6"
                data-testid="go-to-dashboard"
              >
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Link to="/gifts">
                <Button
                  variant="outline"
                  className="w-full border-[#F4F1EA] rounded-full py-6 text-[#3D405B]"
                >
                  <Gift className="w-4 h-4 mr-2" />
                  Send a Gift
                </Button>
              </Link>
            </div>
          </motion.div>
        )}

        {status === "failed" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#E07A5F]/20 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-[#E07A5F]" />
            </div>
            <h1 
              className="text-2xl font-bold text-[#3D405B] mb-2"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              Payment Failed
            </h1>
            <p className="text-[#6D6F7C] mb-6">
              Something went wrong with your payment. Please try again.
            </p>

            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate("/pricing")}
                className="w-full bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full py-6"
              >
                Try Again
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="w-full border-[#F4F1EA] rounded-full py-6 text-[#3D405B]"
              >
                Back to Dashboard
              </Button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccessPage;
